import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/** Data pool cells truncate past this length and reveal the full value on hover. */
const DATA_POOL_CELL_TRUNCATE_LENGTH = 50;

export interface DataPoolCopyableCellProps {
    /** The complete value; only the rendering is truncated, the full text is copied. */
    text: string;
    /** Column heading shown in the tooltip. */
    label: string;
    monospace: boolean;
}

/** Data pool table cell: hovering reveals the full value, clicking copies it to the clipboard. */
export const DataPoolCopyableCell: React.FC<DataPoolCopyableCellProps> = ({
    text,
    label,
    monospace,
}) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const copyValue = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
        } catch (err) {
            console.error('Failed to copy data pool value:', err);
        }
    };

    const value = (
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
            <span
                role="button"
                tabIndex={0}
                aria-label={t('ui.copyToClipboard')}
                onClick={copyValue}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        copyValue();
                    }
                }}
                style={{ cursor: 'pointer' }}
            >
                {text.length > DATA_POOL_CELL_TRUNCATE_LENGTH
                    ? `${text.substring(0, DATA_POOL_CELL_TRUNCATE_LENGTH)}...`
                    : text}
            </span>
            {copied && (
                <span className="textS" style={{ color: '#107c10', whiteSpace: 'nowrap' }}>
                    {t('global.copied')}
                </span>
            )}
        </span>
    );

    if (text.length <= DATA_POOL_CELL_TRUNCATE_LENGTH) return value;

    const tooltipContent = (
        <div>
            <div className="textS" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                {label}
            </div>
            <div
                className="textS"
                style={{ whiteSpace: 'pre-wrap', fontFamily: monospace ? 'monospace' : 'inherit' }}
            >
                {text}
            </div>
            <div className="textS" style={{ marginTop: '8px', fontStyle: 'italic' }}>
                {t('ui.copyToClipboard')}
            </div>
        </div>
    );

    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex min-w-0">{value}</span>
                </TooltipTrigger>
                <TooltipContent sideOffset={8} className="max-w-3xl">
                    {tooltipContent}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};
